import React, { useEffect } from 'react';

import { useUser } from 'contexts/user';

import { User } from 'types/user';

import { request } from 'utils/api';
import { VerifyEmailForm } from 'components/UserVerificationWidget/VerifyEmail';
import { useProvider } from '../useProvider';
import ShortDivider from 'components/ShortDivider';

async function fetchUser(): Promise<User> {
  const { data } = await request({
    method: 'GET',
    path: `/1/users/me`,
  });
  return data;
}

export default function VerifyEmail({
  onVerified,
  journey,
}: {
  onVerified: () => void;
  journey: string;
}) {
  const { user } = useUser();
  const provider = useProvider();
  const [counter, setCounter] = React.useState(0);

  useEffect(() => {
    // Check immediately if the user is already verified
    if (user?.emailVerifiedAt) {
      onVerified();
      return;
    }

    let intervalId: any;
    const startInterval = () => {
      if (intervalId) return;

      intervalId = setInterval(() => {
        // if the user is not verified after ~30 attempts, stop checking
        if (counter > 360) {
          clearInterval(intervalId);
          intervalId = null;
          return;
        }
        setCounter(counter + 1);
        fetchUser().then((user) => {
          if (user.emailVerifiedAt) {
            onVerified();
          }
        });
      }, 5000);
    };

    const stopInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        // Fetch user immediately when tab becomes active
        fetchUser().then((user) => {
          if (user.emailVerifiedAt) {
            onVerified();
            stopInterval();
          } else {
            startInterval();
          }
        });
      } else {
        stopInterval();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (document.visibilityState === 'visible') {
      startInterval();
    }

    // Cleanup
    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.emailVerifiedAt, counter, onVerified]);

  return (
    <>
      <VerifyEmailForm journey={journey} />

      {provider?.supportEmail && (
        <>
          <ShortDivider />
          <p style={{ marginBottom: '1em' }}>
            If you need assistance, please contact our support team at{' '}
            <a target="_blank" href={`mailto:${provider?.supportEmail}`}>
              {provider?.supportEmail}
            </a>
          </p>
        </>
      )}
    </>
  );
}
